// HugeNumber arithmetic is used throughout so big-value experience cannot
// underflow to a regular Number.

import {
  compareHuge,
  huge,
  hugeZero,
  isHugeNumber,
  type HugeNumber,
} from "@/shared/numbers";
import type {
  NHovBranchExperienceByBranch,
  NHovBranchId,
  NHovBranchLevels,
} from "../types";
import { NHOV_BASE_HOV_THRESHOLD_VALUE } from "./nHovConstants";
import type { NHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

// Aliased to the NHov base threshold so a player who just barely qualifies
// still earns a non-zero amount of experience on the first execution.
export const NHOV_BRANCH_EXPERIENCE_REFERENCE_HOV =
  NHOV_BASE_HOV_THRESHOLD_VALUE;

export const NHOV_BRANCH_EXPERIENCE_GAIN_AT_REFERENCE = 1;

export const NHOV_BRANCH_EXPERIENCE_GAIN_EXPONENT = 0.32;

export const NHOV_BRANCH_LEVEL_BASE_EXPERIENCE = 1;

export const NHOV_BRANCH_LEVEL_GROWTH_FACTOR = 2;

const LOG10_GROWTH = Math.log10(NHOV_BRANCH_LEVEL_GROWTH_FACTOR);

export interface CalculateNHovBranchExperienceGainOptions {
  // Current Hov reset loop's earned Hov (`mHov.currentLoopHovEarned`).
  readonly runBaseHov: HugeNumber;
  readonly balance?: NHovBalance;
}

// Mirrors `canExecuteNHov`: sub-threshold reward bases must not accrue any
// experience, so the unlock floor is repeated here.
export function calculateNHovBranchExperienceGain(
  options: CalculateNHovBranchExperienceGainOptions,
): HugeNumber {
  const base = options.runBaseHov;
  const referenceHov =
    options.balance?.baseHovThreshold ?? NHOV_BRANCH_EXPERIENCE_REFERENCE_HOV;
  if (!isHugeNumber(base) || !base.isFinite()) {
    return hugeZero();
  }
  if (compareHuge(base, referenceHov) < 0) {
    return hugeZero();
  }
  const ratio = base.div(referenceHov);
  const compressed = ratio.pow(
    options.balance?.experienceGainExponent ??
      NHOV_BRANCH_EXPERIENCE_GAIN_EXPONENT,
  );
  return compressed.mul(
    options.balance?.experienceGainAtReference ??
      NHOV_BRANCH_EXPERIENCE_GAIN_AT_REFERENCE,
  );
}

export function calculateNHovExperienceForLevel(level: number): HugeNumber {
  if (!Number.isFinite(level) || level <= 0) {
    return hugeZero();
  }
  const growthPow = huge(NHOV_BRANCH_LEVEL_GROWTH_FACTOR).pow(level);
  return growthPow
    .sub(1)
    .div(NHOV_BRANCH_LEVEL_GROWTH_FACTOR - 1)
    .mul(NHOV_BRANCH_LEVEL_BASE_EXPERIENCE);
}

interface CalculateBranchLevelOptions {
  readonly experience: HugeNumber;
}

function calculateBranchContinuousLevel(
  options: CalculateBranchLevelOptions,
): number {
  const experience = options.experience;
  if (!isHugeNumber(experience) || !experience.isFinite()) return 0;
  if (compareHuge(experience, 0) <= 0) {
    return 0;
  }
  const inner = experience
    .mul(NHOV_BRANCH_LEVEL_GROWTH_FACTOR - 1)
    .div(NHOV_BRANCH_LEVEL_BASE_EXPERIENCE)
    .add(1);
  const logInner = inner.log10().toNumber();
  if (!Number.isFinite(logInner) || logInner <= 0) return 0;
  const level = logInner / LOG10_GROWTH;
  return level >= 0 ? level : 0;
}

// log10(experience).toNumber() stays in a safe Number range even for
// astronomically large experience because it is logarithmic.
function calculateBranchLevel(options: CalculateBranchLevelOptions): number {
  const continuousLevel = calculateBranchContinuousLevel(options);
  if (!Number.isFinite(continuousLevel) || continuousLevel <= 0) return 0;
  const level = Math.floor(continuousLevel);
  return level >= 0 ? level : 0;
}

export interface CalculateNHovBranchLevelOptions {
  readonly experience: HugeNumber;
}

export function calculateNHovBranchLevel(
  options: CalculateNHovBranchLevelOptions,
): number {
  return calculateBranchLevel(options);
}

export type NHovBranchContinuousLevels = Readonly<Record<NHovBranchId, number>>;

export function calculateNHovBranchContinuousLevel(
  options: CalculateNHovBranchLevelOptions,
): number {
  return calculateBranchContinuousLevel(options);
}

export interface CalculateNHovBranchLevelProgressRatioOptions {
  readonly experience: HugeNumber;
}

// Presentation-only XP gauge ratio. Degenerate ranges collapse to 0 so the
// bar reads empty rather than full when a next level cannot be resolved.
export function calculateNHovBranchLevelProgressRatio(
  options: CalculateNHovBranchLevelProgressRatioOptions,
): number {
  const experience = options.experience;
  if (!isHugeNumber(experience) || !experience.isFinite()) return 0;
  if (compareHuge(experience, 0) <= 0) return 0;
  const currentLevel = calculateBranchLevel({ experience });
  if (!Number.isFinite(currentLevel) || currentLevel < 0) return 0;
  const prevThreshold = calculateNHovExperienceForLevel(currentLevel);
  const nextThreshold = calculateNHovExperienceForLevel(currentLevel + 1);
  const range = nextThreshold.sub(prevThreshold);
  if (!range.isFinite() || compareHuge(range, 0) <= 0) return 0;
  const progress = experience.sub(prevThreshold).div(range).toNumber();
  if (!Number.isFinite(progress) || progress <= 0) return 0;
  if (progress >= 1) return 1;
  return progress;
}

export interface CalculateProspectiveNHovBranchExperienceOptions {
  readonly currentExperience: HugeNumber;
  readonly runBaseHov: HugeNumber;
  readonly balance?: NHovBalance;
}

export interface NHovBranchExperienceGrowth {
  readonly experienceGain: HugeNumber;
  readonly experienceAfter: HugeNumber;
}

export function resolveNHovBranchExperienceGrowth(
  options: CalculateProspectiveNHovBranchExperienceOptions,
): NHovBranchExperienceGrowth {
  const experienceGain = calculateNHovBranchExperienceGain({
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  });
  const experienceAfter = options.currentExperience.add(experienceGain);
  return {
    experienceGain,
    experienceAfter,
  };
}

// Single source of truth for the prospective experience used by both the
// preview branch level and the UI progress bar so they cannot drift.
export function calculateProspectiveNHovBranchExperience(
  options: CalculateProspectiveNHovBranchExperienceOptions,
): HugeNumber {
  return resolveNHovBranchExperienceGrowth(options).experienceAfter;
}

export type CalculateProspectiveNHovBranchLevelOptions =
  CalculateProspectiveNHovBranchExperienceOptions;

export function calculateProspectiveNHovBranchLevel(
  options: CalculateProspectiveNHovBranchLevelOptions,
): number {
  return calculateNHovBranchLevel({
    experience: calculateProspectiveNHovBranchExperience(options),
  });
}

export function resolveNHovBranchLevelsFromExperience(
  branchExperienceByBranch: NHovBranchExperienceByBranch,
): NHovBranchLevels {
  return {
    hgAmplify: calculateNHovBranchLevel({
      experience: branchExperienceByBranch.hgAmplify,
    }),
    hgDiscount: calculateNHovBranchLevel({
      experience: branchExperienceByBranch.hgDiscount,
    }),
    hngScale: calculateNHovBranchLevel({
      experience: branchExperienceByBranch.hngScale,
    }),
    hgNHovEffectsToHng: calculateNHovBranchLevel({
      experience: branchExperienceByBranch.hgNHovEffectsToHng,
    }),
  };
}

export function resolveNHovBranchContinuousLevelsFromExperience(
  branchExperienceByBranch: NHovBranchExperienceByBranch,
): NHovBranchContinuousLevels {
  return {
    hgAmplify: calculateNHovBranchContinuousLevel({
      experience: branchExperienceByBranch.hgAmplify,
    }),
    hgDiscount: calculateNHovBranchContinuousLevel({
      experience: branchExperienceByBranch.hgDiscount,
    }),
    hngScale: calculateNHovBranchContinuousLevel({
      experience: branchExperienceByBranch.hngScale,
    }),
    hgNHovEffectsToHng: calculateNHovBranchContinuousLevel({
      experience: branchExperienceByBranch.hgNHovEffectsToHng,
    }),
  };
}
